import { Stack } from '@mui/material';
import { ComponentType, ReactNode, SVGProps } from 'react';

import { ImageThumbnail } from '@/components/display/ImageThumbnail/ImageThumbnail';
import { TEMPLATE_DETAILS } from '@/modules/artifactStudio/DiagramBuilder.constants';
import {
  TEMPLATE_PROMPT_CARD_ART_SIZE,
  TemplatePromptCard,
} from '@/modules/common/Chat/ChatMessage/TemplatePromptCard';
import { UserMessageBubble } from '@/modules/common/Chat/ChatMessage/UserMessageBubble';
import { MEMO_PROMPT_CONFIG } from '@/modules/memos/memos.constants';
import { NO_DOCUMENTS_PLAN_TEMPLATES } from '@/modules/onboarding/smartReview/steps/noDocumentsPlan/noDocumentsPlanTemplates';
import {
  DiagramArtifactPromptDiagramArtifactPromptType,
  MemoPromptMemoPromptType,
  MessageWithPromptTemplate,
  NoDocumentsOnboardingPromptType,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { HouseholdChatMessage } from '../contexts/householdChat.context';
import { TEMPLATE_ONLY_MESSAGE_CONTENT } from '../contexts/householdChatTranscript.utils';

interface TemplatePromptCardContent {
  description: string;
  icon: ReactNode;
  title: string;
}

function fromIllustration({
  description,
  Illustration,
  title,
}: {
  description: string;
  Illustration: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
}): TemplatePromptCardContent {
  return {
    description,
    title,
    // The template art is centered in a wide frame with empty space on each
    // side. "slice" fills the panel height and crops that empty space, so the
    // art is not shrunk to fit the full frame width.
    icon: (
      <Illustration
        {...TEMPLATE_PROMPT_CARD_ART_SIZE}
        preserveAspectRatio="xMidYMid slice"
        style={{ display: 'block' }}
      />
    ),
  };
}

function fromMemoPrompt(
  promptType: keyof typeof MEMO_PROMPT_CONFIG
): TemplatePromptCardContent {
  const { description, Icon, title } = MEMO_PROMPT_CONFIG[promptType];
  return { description, title, icon: <Icon size={24} /> };
}

function fromNoDocumentsPlan(
  promptType: NoDocumentsOnboardingPromptType
): TemplatePromptCardContent {
  return fromIllustration(NO_DOCUMENTS_PLAN_TEMPLATES[promptType]);
}

function fromDiagramTemplate(
  promptType: keyof typeof TEMPLATE_DETAILS
): TemplatePromptCardContent {
  return fromIllustration(TEMPLATE_DETAILS[promptType]);
}

/**
 * The card content for each message template. CUSTOM has no card because it
 * is not a template that a user selects.
 */
const TEMPLATE_PROMPT_CARDS: Record<
  MessageWithPromptTemplate,
  TemplatePromptCardContent | null
> = {
  [MessageWithPromptTemplate.Custom]: null,
  [MessageWithPromptTemplate.CoverLetter]: fromMemoPrompt(
    MemoPromptMemoPromptType.CoverLetter
  ),
  [MessageWithPromptTemplate.EstatePlanSummary]: fromMemoPrompt(
    MemoPromptMemoPromptType.EstatePlanSummary
  ),
  [MessageWithPromptTemplate.ExecutiveSummary]: fromMemoPrompt(
    MemoPromptMemoPromptType.ExecutiveSummary
  ),
  [MessageWithPromptTemplate.RoleSummary]: fromMemoPrompt(
    MemoPromptMemoPromptType.RoleSummary
  ),
  [MessageWithPromptTemplate.TrustComparison]: fromMemoPrompt(
    MemoPromptMemoPromptType.TrustComparison
  ),
  [MessageWithPromptTemplate.EstateDistributionDetailed]: fromDiagramTemplate(
    DiagramArtifactPromptDiagramArtifactPromptType.EstateDistributionDetailed
  ),
  [MessageWithPromptTemplate.EstateDistributionSimple]: fromDiagramTemplate(
    DiagramArtifactPromptDiagramArtifactPromptType.EstateDistributionSimple
  ),
  [MessageWithPromptTemplate.AbJointTrust]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.AbJointTrust
  ),
  [MessageWithPromptTemplate.AbSeparateTrusts]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.AbSeparateTrusts
  ),
  [MessageWithPromptTemplate.AbcJointTrust]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.AbcJointTrust
  ),
  [MessageWithPromptTemplate.AbcSeparateTrusts]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.AbcSeparateTrusts
  ),
  [MessageWithPromptTemplate.EqualShares]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.EqualShares
  ),
  [MessageWithPromptTemplate.FamilyTrust]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.FamilyTrust
  ),
  [MessageWithPromptTemplate.SpecificBequests]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.SpecificBequests
  ),
  [MessageWithPromptTemplate.SurvivingSpouse]: fromNoDocumentsPlan(
    NoDocumentsOnboardingPromptType.SurvivingSpouse
  ),
};

/** The alternative text of every image thumbnail in a user message. */
const IMAGE_THUMBNAIL_ALT = 'Uploaded image';

/**
 * A user message in the household chat transcript. It shows the template
 * card of a message sent with a template, then thumbnails of the images that
 * the user attached, then the text that the user typed.
 */
export function HouseholdChatUserMessage({
  message,
}: {
  message: Pick<
    HouseholdChatMessage,
    | 'content'
    | 'pendingUploadedImages'
    | 'userUploadedImages'
    | 'withPromptTemplate'
  >;
}) {
  const template = message.withPromptTemplate
    ? TEMPLATE_PROMPT_CARDS[message.withPromptTemplate]
    : null;
  const storedImages = getNodes(message.userUploadedImages);
  const pendingImages = message.pendingUploadedImages ?? [];

  // Keep the fallback in older template-only messages hidden too.
  const userText = !template
    ? message.content
    : message.content === TEMPLATE_ONLY_MESSAGE_CONTENT ||
        message.content === 'Create an artifact from the selected template.'
      ? ''
      : message.content.trim();
  return (
    <Stack gap={0.5}>
      {template && <TemplatePromptCard {...template} />}
      {(storedImages.length > 0 || pendingImages.length > 0) && (
        <Stack
          direction="row"
          gap={0.5}
          flexWrap="wrap"
          justifyContent="flex-end"
          sx={{ alignSelf: 'flex-end', maxWidth: '80%' }}
        >
          {storedImages.map((image) => (
            <ImageThumbnail
              key={image.id}
              source={{ imageId: image.id }}
              alt={IMAGE_THUMBNAIL_ALT}
            />
          ))}
          {pendingImages.map((file, index) => (
            <ImageThumbnail
              key={index}
              source={{ file }}
              alt={IMAGE_THUMBNAIL_ALT}
            />
          ))}
        </Stack>
      )}
      {userText && <UserMessageBubble content={userText} />}
    </Stack>
  );
}
